import React, {useEffect, useRef} from "react";
import {PointerProvider} from "../contexts/Pointer";
import BalloonEventsCanvas from "./BalloonEventsCanvas";
import BalloonEventsMapBox, {useMapBox} from "./BalloonEventsMapBox";
import PolygonHighlight from './PolygonHighlight';
import {useLocation, useNavigate} from "react-router";
import {useMapBoundingBoxRef} from "../contexts/MapBoundingBox";
import {useBalloonEventsOptions} from "../contexts/EmbedOptions";

export function BalloonEventsMap() {
    const location = useLocation();
    const navigate = useNavigate();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const setMapRef = useMapBoundingBoxRef();
    const {hideUI, onMapClick} = useBalloonEventsOptions();

    const isMain = location.pathname === "/";
    // When a click handler is supplied (e.g. the embedded mini-map), the whole
    // surface becomes a single click target: the inner map is non-interactive so
    // the click bubbles to the container and `onMapClick` takes over.
    const captureClick = !!onMapClick;
    // Interactive (pannable) on the main map and on bare hideUI maps; not when we
    // capture clicks, and not on the full app's detail routes (which return to map).
    const interactive = (isMain || hideUI) && !captureClick;
    const mapBoxInfo = useMapBox();
    const map = mapBoxInfo?.map;

    useEffect(() => {
        if (isMain && map) map.resize();
    }, [location.pathname]);

    // Register map container for bounding box tracking
    useEffect(() => {
        if (mapContainerRef.current) {
            setMapRef(mapContainerRef.current);
        }
    }, [setMapRef]);

    const handleClick = () => {
        if (captureClick) {
            onMapClick!();
            return;
        }
        if (interactive) return;
        navigate('/');
    };

    return (
        <div
            ref={mapContainerRef}
            className={`balloon-events-map ${
                interactive
                    ? 'w-full h-full'
                    : captureClick
                        ? 'w-full h-full cursor-pointer'
                        : 'w-full h-full lg:cursor-pointer lg:rounded-lg lg:overflow-hidden lg:shadow-lg'
            }`}
            onClick={handleClick}
        >
            <div
                className={
                    interactive ? 'w-full h-full' : 'pointer-events-none w-full h-full'
                }
            >
                <PointerProvider>
                    <BalloonEventsMapBox.Container/>
                    <BalloonEventsCanvas/>
                    <PolygonHighlight/>
                </PointerProvider>
            </div>
        </div>
    );
}
